import { createFeatureSelector, createSelector } from '@ngrx/store';
import { FolderType, IDocument, IPhoto, IVideo } from 'src/app/core/models/resource';
import { selectSelectedStudentId } from '../students/student.selectors';
import { LibraryState, ResourceState } from './resource.reducer';

export const selectResourceState = createFeatureSelector<ResourceState>('resources');

// ── School / Folder selectors ──

export const selectSelectedSchoolCode = createSelector(
  selectResourceState,
  (state: ResourceState) => state.selectedSchoolCode,
);

export const selectFoldersForSchool = (schoolCode: string) =>
  createSelector(selectResourceState, (state: ResourceState) => state.folders[schoolCode] ?? []);

export const selectSelectedSchoolFolders = createSelector(selectResourceState, (state: ResourceState) =>
  state.selectedSchoolCode ? (state.folders[state.selectedSchoolCode] ?? []) : [],
);

export const selectSelectedSchoolFoldersByType = (folderType: FolderType) =>
  createSelector(selectSelectedSchoolFolders, (folders) => folders.filter((f) => f.folderType === folderType));

export const selectFoldersByType = (schoolCode: string, folderType: FolderType) =>
  createSelector(selectFoldersForSchool(schoolCode), (folders) =>
    folders.filter((folder) => folder.folderType === folderType),
  );

export const selectFoldersLoading = createSelector(selectResourceState, (state: ResourceState) => state.foldersLoading);

export const selectFoldersError = createSelector(selectResourceState, (state: ResourceState) => state.foldersError);

// ── Folder resources selectors ──

export const selectSelectedFolderId = createSelector(
  selectResourceState,
  (state: ResourceState) => state.selectedFolderId,
);

export const selectSelectedFolder = createSelector(selectResourceState, (state: ResourceState) => {
  if (!state.selectedSchoolCode || !state.selectedFolderId) {
    return null;
  }
  const folders = state.folders[state.selectedSchoolCode] ?? [];
  return folders.find((f) => f.id === state.selectedFolderId) ?? null;
});

export const selectFolderResources = (folderId: string) =>
  createSelector(selectResourceState, (state: ResourceState) => state.folderResources[folderId] ?? []);

export const selectSelectedFolderResources = createSelector(selectResourceState, (state: ResourceState) =>
  state.selectedFolderId ? (state.folderResources[state.selectedFolderId] ?? []) : [],
);

export const selectSelectedFolderPhotos = createSelector(selectSelectedFolderResources, (resources) =>
  resources.filter((r): r is IPhoto => r.type === 'Photo'),
);

export const selectSelectedFolderDocuments = createSelector(selectSelectedFolderResources, (resources) =>
  resources.filter((r): r is IDocument => r.type === 'Document'),
);

export const selectSelectedFolderVideos = createSelector(selectSelectedFolderResources, (resources) =>
  resources.filter((r): r is IVideo => r.type === 'Video'),
);

export const selectFolderResourcesLoading = createSelector(
  selectResourceState,
  (state: ResourceState) => state.folderResourcesLoading,
);

export const selectFolderResourcesError = createSelector(
  selectResourceState,
  (state: ResourceState) => state.folderResourcesError,
);

export const selectSelectedFolderName = createSelector(selectSelectedFolder, (folder) => folder?.name ?? '');

export const selectSelectedFolderResourceCount = createSelector(
  selectSelectedFolderResources,
  (resources) => resources.length,
);

// ── Report selectors ──

export const selectAllReports = createSelector(selectResourceState, (state: ResourceState) => state.reports);

export const selectReportsLoading = createSelector(selectResourceState, (state: ResourceState) => state.reportsLoading);

export const selectReportsError = createSelector(selectResourceState, (state: ResourceState) => state.reportsError);

export const selectStudentReports = (studentId: string) =>
  createSelector(selectAllReports, (reports) => reports[studentId] || []);

export const selectSelectedStudentReports = () =>
  createSelector(selectAllReports, selectSelectedStudentId, (reports, studentId) =>
    studentId ? reports[studentId] || [] : [],
  );

export const selectSelectedStudentReportsSorted = () =>
  createSelector(selectSelectedStudentReports(), (reports) =>
    [...reports].sort((a, b) => {
      const yearA = Number.parseInt(a.year || '0');
      const yearB = Number.parseInt(b.year || '0');
      if (yearA !== yearB) {
        return yearB - yearA;
      }
      const termA = Number.parseInt(a.term || '0');
      const termB = Number.parseInt(b.term || '0');
      return termB - termA;
    }),
  );

export const selectStudentHasReports = (studentId: string) =>
  createSelector(selectStudentReports(studentId), (reports) => reports.length > 0);

// ── News selectors ──

export const selectAllNews = createSelector(selectResourceState, (state: ResourceState) => state.news);

export const selectNewsLoading = createSelector(selectResourceState, (state: ResourceState) => state.newsLoading);

export const selectNewsError = createSelector(selectResourceState, (state: ResourceState) => state.newsError);

export const selectNewsArticlesSorted = createSelector(selectAllNews, (news) =>
  [...news].sort((a, b) => {
    return new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime();
  }),
);

export const selectSixLatestNewsArticles = createSelector(selectNewsArticlesSorted, (news) => news.slice(0, 6));

//==============================================================================================================================
// Target the global feature node
export const selectLibraryState = createFeatureSelector<LibraryState>('library');

export const selectAllLibraries = createSelector(
  selectLibraryState,
  (state) => state.libraries
);

/**
 * Parameterized selector to grab a singular school block layout by its code string identifier
 */
export const selectSchoolLibraryByCode = (schoolCode: string) =>
  createSelector(
    selectAllLibraries,
    (libraries) => libraries.find((lib) => lib.code.toLowerCase() === schoolCode.toLowerCase()) || null
  );
